import type { Recordable } from '@vben/types';

import type { AuthApi } from '#/api';

import { computed, ref } from 'vue';

import { updatePreferences } from '@vben/preferences'; // AI-Generated by Claude · 分销商多域名登录

import { defineStore } from 'pinia';

import { recognizeDistributorBrandBySubdomainApi } from '#/api';

const DEFAULT_LOGIN_LOGO = '/static/logo.png';
const DEFAULT_FAVICON = '/favicon.ico';
const DEFAULT_APP_NAME = import.meta.env.VITE_APP_TITLE || '速蜂';
const DEFAULT_THEME_COLOR = 'hsl(30, 100%, 50%)'; // 平台默认橙色 // AI-Generated by Claude · 分销商多域名登录 商户端主题色

export function updateDocumentFavicon(url?: string) {
  if (typeof document === 'undefined') return;

  let favicon = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
  if (!favicon) {
    favicon = document.createElement('link');
    favicon.rel = 'icon';
    document.head.append(favicon);
  }
  favicon.href = url || DEFAULT_FAVICON;
}

export function resolveBrandAssetUrl(url?: string) {
  if (!url || /^(?:[a-z][a-z\d+.-]*:)?\/\//i.test(url)) {
    return url || '';
  }
  const assetOrigin = (
    import.meta.env.VITE_BRAND_ASSET_ORIGIN ||
    import.meta.env.VITE_GLOB_API_URL ||
    ''
  ).trim();
  if (!/^https?:\/\//i.test(assetOrigin)) {
    return url;
  }
  return new URL(url, assetOrigin).toString();
}

function isLocalHost(hostname: string) {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    /^\d{1,3}(?:\.\d{1,3}){3}$/.test(hostname)
  );
}

function querySubdomainPrefix() {
  if (typeof window === 'undefined') return '';
  return (
    new URLSearchParams(window.location.search).get('subdomainPrefix') ?? ''
  );
}

// 导出供退出登录等场景复用，统一识别规则 // AI-Generated by Claude · 分销商多域名登录 T7
export function currentHostSubdomainPrefix() {
  if (typeof window === 'undefined') return ''; // AI-Generated by Claude · 分销商多域名登录

  // 优先级1：URL query ?subdomainPrefix=xxx（本地调试） // AI-Generated by Claude · 分销商多域名登录
  const fromQuery = querySubdomainPrefix().trim(); // AI-Generated by Claude · 分销商多域名登录
  if (fromQuery) return fromQuery; // AI-Generated by Claude · 分销商多域名登录

  const hostname = window.location.hostname.toLowerCase(); // AI-Generated by Claude · 分销商多域名登录
  // localhost / 127.0.0.1 / 纯 IP → 总平台直属，无分销商品牌 // AI-Generated by Claude · 分销商多域名登录
  if (isLocalHost(hostname)) return ''; // AI-Generated by Claude · 分销商多域名登录

  // 商户端域名形态：{前缀}-b.主域名（生产）/ pre-{前缀}-b.主域名（测试，去 pre- 前缀） // AI-Generated by Claude · 分销商多域名登录
  let first = hostname.split('.')[0] ?? ''; // AI-Generated by Claude · 分销商多域名登录
  if (first.startsWith('pre-')) first = first.slice(4); // 去测试环境 pre- 前缀 // AI-Generated by Claude · 分销商多域名登录
  if (first.endsWith('-b') && first.length > 2) {
    return first.slice(0, -2); // 去掉结尾 -b 得分销商前缀 // AI-Generated by Claude · 分销商多域名登录
  }

  // 否则（如 pre-b / b.主域名 / 总平台）无分销商品牌 // AI-Generated by Claude · 分销商多域名登录
  return ''; // AI-Generated by Claude · 分销商多域名登录
}

export const useDistributorBrandStore = defineStore('distributorBrand', () => {
  const settings = ref<AuthApi.DistributorRegistrationSettings | null>(null);
  const subdomainPrefix = ref('');
  const loading = ref(false);
  const loaded = ref(false);
  const logoLoadFailed = ref(false);
  let logoValidationSequence = 0;

  const loginLogoUrl = computed(() =>
    settings.value?.loginLogoUrl && !logoLoadFailed.value
      ? resolveBrandAssetUrl(settings.value.loginLogoUrl)
      : DEFAULT_LOGIN_LOGO,
  );
  const loginDisplayName = computed(
    () => settings.value?.loginDisplayName || '',
  );
  const loginSubtitle = computed(() => settings.value?.loginSubtitle || '');
  const loginBgImageUrl = computed(() =>
    resolveBrandAssetUrl(settings.value?.loginBgImageUrl),
  );
  const hasDistributorBrand = computed(() => Boolean(settings.value));

  function validateLogoAndUpdateFavicon(url?: string) {
    const sequence = ++logoValidationSequence;
    const candidate = resolveBrandAssetUrl(url);
    logoLoadFailed.value = false;

    if (!candidate) {
      updateDocumentFavicon();
      return;
    }
    if (typeof window === 'undefined' || typeof window.Image !== 'function') {
      updateDocumentFavicon(candidate);
      return;
    }

    const image = new window.Image();
    image.addEventListener('load', () => {
      if (sequence !== logoValidationSequence) return;
      logoLoadFailed.value = false;
      updateDocumentFavicon(candidate);
    });
    image.addEventListener('error', () => {
      if (sequence !== logoValidationSequence) return;
      logoLoadFailed.value = true;
      updateDocumentFavicon();
    });
    image.src = candidate;
  }

  function applySettings(
    nextSettings?: AuthApi.DistributorRegistrationSettings | null,
  ) {
    settings.value = nextSettings ?? null;
    const brandName = settings.value?.loginDisplayName?.trim();
    updatePreferences({
      app: { name: brandName || DEFAULT_APP_NAME },
      theme: {
        colorPrimary: settings.value?.themeColor || DEFAULT_THEME_COLOR,
      },
    });
    validateLogoAndUpdateFavicon(settings.value?.loginLogoUrl);
  }

  async function loadByCurrentHost() {
    if (loaded.value) return settings.value;

    const prefix = currentHostSubdomainPrefix();
    subdomainPrefix.value = prefix;
    if (!prefix) {
      loaded.value = true;
      // 总平台无分销商品牌：重置名称、Logo/favicon 和主题色，避免残留上一分销商缓存
      applySettings(null);
      return null;
    }

    loading.value = true;
    try {
      applySettings(await recognizeDistributorBrandBySubdomainApi(prefix));
    } catch {
      applySettings(null);
    } finally {
      loading.value = false;
      loaded.value = true;
    }
    return settings.value;
  }

  function appendRegistrationContext(values: Recordable<any>) {
    if (subdomainPrefix.value && !values.inviteCode) {
      values.subdomainPrefix = subdomainPrefix.value;
    }
    return values;
  }

  return {
    appendRegistrationContext,
    applySettings,
    hasDistributorBrand,
    loadByCurrentHost,
    loaded,
    loading,
    loginBgImageUrl, // AI-Generated by Claude · 分销商多域名登录
    loginDisplayName,
    loginLogoUrl,
    loginSubtitle,
    settings,
    subdomainPrefix,
  };
});
